import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../lib/supabase';
import { Car, Upload, X } from 'lucide-react';

interface ProfilePhotoProps {
  url: string | null;
  size?: 'sm' | 'md' | 'lg';
  onUpdate: (url: string) => void;
  editable?: boolean;
}

const DEFAULT_PHOTO = 'https://images.unsplash.com/photo-1511527844068-006b95d162c2?auto=format&fit=crop&q=80&w=200&h=200';

const sizeClasses = {
  sm: 'h-7 w-7',
  md: 'h-10 w-10',
  lg: 'h-12 w-12'
};

export default function ProfilePhoto({ url, size = 'md', onUpdate, editable = false }: ProfilePhotoProps) {
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);
      const file = e.target.files?.[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('Please upload an image file');
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('File size must be less than 5MB');
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      // Delete old photo if exists
      if (url) {
        const oldPath = url.split('/').pop();
        if (oldPath) {
          await supabase.storage
            .from('profile_photos')
            .remove([`${user.id}/${oldPath}`]);
        }
      }

      // Upload new photo
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}${Date.now()}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('profile_photos')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl }, error: urlError } = supabase.storage
        .from('profile_photos')
        .getPublicUrl(filePath);

      if (urlError) throw urlError;

      // Update settings with new photo URL
      const { error: updateError } = await supabase
        .from('settings')
        .update({ profile_photo_url: publicUrl })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      onUpdate(publicUrl);
      setShowUploadModal(false);
    } catch (error: any) {
      console.error('Error uploading photo:', error);
      alert(error.message);
    } finally {
      setUploading(false);
    }
  };

  const photoUrl = url || DEFAULT_PHOTO;

  return (
    <>
      <div 
        className={`relative rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden flex-shrink-0 ${sizeClasses[size]} ${editable ? 'cursor-pointer' : ''}`}
        onClick={() => editable && setShowUploadModal(true)}
      >
        {photoUrl === DEFAULT_PHOTO ? (
          <div className="h-full w-full flex items-center justify-center bg-blue-500">
            <Car className="h-4 w-4 text-white" />
          </div>
        ) : (
          <img
            src={photoUrl}
            alt="Profile"
            className="h-full w-full object-cover"
          />
        )}
      </div>

      {showUploadModal && createPortal(
        <div className="fixed inset-0 z-[99999]">
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[99999]"
            onClick={() => setShowUploadModal(false)}
            aria-hidden="true"
          />

          <div className="fixed inset-0 overflow-y-auto z-[100000]">
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full z-[100001]">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Profielfoto wijzigen
                  </h3>
                  <button
                    onClick={() => setShowUploadModal(false)}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="p-6">
                  <div className="flex items-center justify-center">
                    <div className="h-32 w-32 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
                      {photoUrl === DEFAULT_PHOTO ? (
                        <div className="h-full w-full flex items-center justify-center bg-blue-500">
                          <Car className="h-12 w-12 text-white" />
                        </div>
                      ) : (
                        <img
                          src={photoUrl}
                          alt="Current profile"
                          className="h-full w-full object-cover"
                        />
                      )}
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nieuwe foto uploaden
                    </label>
                    <label className="relative block">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        disabled={uploading}
                        className="sr-only"
                      />
                      <div className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                        {uploading ? (
                          <div className="flex items-center">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current mr-2"></div>
                            Uploaden...
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <Upload className="h-5 w-5 mr-2" />
                            Kies een foto
                          </div>
                        )}
                      </div>
                    </label>
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      PNG, JPG of GIF (max. 5MB)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}